import PropTypes from 'prop-types';

import {
  Box, Flex, Heading, SimpleGrid, Skeleton, Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogSummary} from '@amblique/king-living/app/components/contentful/blog-summary';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';
import {useResolvedInlinedBlogGridCtaBlogSysId} from '@amblique/king-living/app/hooks/use-resolved-inline-blog-grid-cta-sys-id';

/**
 * Row pattern for Style 2 layout (no repeat):
 * - Row 1: 2 columns (65% left, 35% right) - 2 items
 * - Row 2: 3 columns equal - 3 items
 */
const ROW_PATTERN = [
  {type: 'twoColLeft', count: 2},
  {type: 'threeCol', count: 3}
];

/**
 * Split blogs into rows based on the fixed pattern (no repeat)
 * @param {Array} blogs - Array of blog items
 * @returns {Array} - Array of row objects with type and items
 */
const splitIntoRows = (blogs) => {
  const rows = [];
  let currentIndex = 0;

  // Only iterate through the pattern once (no repeat)
  for (const pattern of ROW_PATTERN) {
    if (currentIndex >= blogs.length) break;

    const rowItems = blogs.slice(currentIndex, currentIndex + pattern.count);

    if (rowItems.length > 0) {
      rows.push({
        type: pattern.type,
        items: rowItems,
      });
    }

    currentIndex += pattern.count;
  }

  return rows;
};

/**
 * BlogGrid Style 2 - King Journal (Blog Listing Page)
 * Fixed layout (no repeat): 2-col (65% left, 35% right) → 3-col equal
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - BlogGrid component data
 * @param {string} [props.component.heading] - Section heading (storefront)
 * @param {string} [props.component.title] - Entry title (CMS only, not rendered)
 * @param {string} props.component.description - Section description
 * @param {Object[]} props.component.blogSummaryCollection - Collection of blog summaries
 * @param {Object} props.component.cta - CTA link component
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {Object} props.styles - Component styles
 * @returns {React.ReactElement}
 */
export const BlogGridStyle2 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {description, blogSummaryCollection, cta} = component;
  const headingText = typeof component?.heading === 'string' ? component.heading.trim() : '';
  const blogSummaries = blogSummaryCollection?.items || [];

  const inlinedBlogGridCtaBlogSysId = useResolvedInlinedBlogGridCtaBlogSysId(cta, blogSummaries);

  // Split blogs into rows based on pattern
  const rows = splitIntoRows(blogSummaries);

  return (
    <Box {...styles.container} className="blog-grid__container">
      {(headingText || description) && (
        <Flex
          {...styles.headerRow}
          className={['blog-grid__header', styles.headerRow?.className].filter(Boolean).join(' ')}
        >
          <Box {...styles.headingRegion}>
            {headingText ? <Heading {...styles.title}>{headingText}</Heading> : null}
          </Box>
          {description ? <Text {...styles.description}>{description}</Text> : null}
        </Flex>
      )}

      {/* Dynamic Rows */}
      {!isComplete && isFetching ? (
        <Skeleton height="600px" width="100%" />
      ) : (
        rows.map((row, rowIndex) => {
          // 2-column row (65% left, 35% right)
          if (row.type === 'twoColLeft') {
            return (
              <Flex
                key={`row-${rowIndex}`}
                className="blog-grid-listing__row--twoColLeft"
                {...styles.twoColRow}
              >
                {row.items.map((blog, index) => (
                  <Box
                    key={blog.sys?.id || `${rowIndex}-${index}`}
                    {...(index === 0 ? styles.twoColLarge : styles.twoColSmall)}
                  >
                    <Component
                      as={BlogSummary}
                      component={blog}
                      gridSectionCta={
                        inlinedBlogGridCtaBlogSysId &&
                        blog?.sys?.id &&
                        inlinedBlogGridCtaBlogSysId === blog.sys.id
                          ? cta
                          : undefined
                      }
                      isComplete={isComplete}
                      isFetching={isFetching}
                      slug={blog.pageSlug}
                    />
                  </Box>
                ))}
              </Flex>
            );
          }

          // 3-column row (equal)
          return (
            <SimpleGrid
              key={`row-${rowIndex}`}
              {...styles.threeColRow}
              className="blog-grid__posts blog-grid-listing__row--three-col"
            >
              {row.items.map((blog, index) => (
                <Component
                  key={blog.sys?.id || `${rowIndex}-${index}`}
                  as={BlogSummary}
                  component={blog}
                  gridSectionCta={
                    inlinedBlogGridCtaBlogSysId &&
                    blog?.sys?.id &&
                    inlinedBlogGridCtaBlogSysId === blog.sys.id
                      ? cta
                      : undefined
                  }
                  isComplete={isComplete}
                  isFetching={isFetching}
                  slug={blog.pageSlug}
                />
              ))}
            </SimpleGrid>
          );
        })
      )}

      {/* CTA Link (theme: contentful/blog-grid Style 2 → ctaContainer) */}
      {cta && !inlinedBlogGridCtaBlogSysId ? (
        <Box
          {...styles.ctaContainer}
          className={
            ['blog-grid-listing__cta', styles.ctaContainer?.className].filter(Boolean).join(' ')
          }
        >
          <ContentfulLink component={cta} isComplete={isComplete} isFetching={isFetching} />
        </Box>
      ) : null}
    </Box>
  );
};

BlogGridStyle2.displayName = 'BlogGridStyle2';
BlogGridStyle2.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
